import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Empty, StatusBadge, CopyButton } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Webhooks() {
  const [hooks, setHooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [url, setUrl] = useState("");
  const load = () => { api.get("/merchant/webhooks").then((r) => setHooks(r.data)); api.get("/merchant/webhook-deliveries").then((r) => setLogs(r.data)); api.get("/merchant/webhook-inbox").then((r) => setInbox(r.data)); };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const addSink = async () => { try { await api.post("/merchant/webhooks/sink"); toast.success("Récepteur de test activé"); load(); } catch (e) { toast.error(errMsg(e)); } };

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/merchant/webhooks", { url }); setUrl(""); toast.success("Webhook enregistré"); load(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const ping = async (h) => { const { data } = await api.post(`/merchant/webhooks/${h.id}/ping`); toast[data.status === "delivered" ? "success" : "error"](`Ping : ${data.status} ${data.last_status_code || data.last_error || ""}`); load(); };
  const del = async (h) => { if (!window.confirm("Supprimer ce webhook ?")) return; await api.delete(`/merchant/webhooks/${h.id}`); load(); };
  const resend = async (d) => { const { data } = await api.post(`/merchant/webhook-deliveries/${d.id}/resend`); toast[data.status === "delivered" ? "success" : "error"](`Renvoi : ${data.status}`); load(); };

  return (
    <div>
      <PageHeader title="Webhooks" subtitle="Événements : order.pending, order.detected, order.confirmed, order.expired, order.underpaid, order.overpaid. Signature HMAC-SHA256 dans X-Gateway-Signature (t=…,v1=…), retries avec backoff 1m/5m/30m/2h/12h." />
      <form onSubmit={add} className="panel p-5 flex flex-col sm:flex-row gap-3 mb-6">
        <Input type="url" required placeholder="https://votre-site.com/webhooks/btc" value={url} onChange={(e) => setUrl(e.target.value)} className="font-mono" data-testid="webhook-url-input" />
        <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold" data-testid="save-webhook-btn">Ajouter</Button>
        <Button type="button" variant="secondary" onClick={addSink} data-testid="add-sink-webhook-btn">Récepteur de test intégré</Button>
      </form>
      <div className="space-y-3 mb-8">
        {hooks.length === 0 ? <Empty text="Aucun webhook configuré." /> : hooks.map((h) => (
          <div key={h.id} className="panel p-4" data-testid="webhook-row">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="font-mono text-sm break-all">{h.url}{h.is_sink && <span className="ml-2 text-[10px] font-mono px-2 py-0.5 rounded-full border text-blue-400 border-blue-500/30">récepteur de test</span>}</div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="secondary" onClick={() => ping(h)} data-testid="ping-webhook-btn">Envoyer un ping</Button>
                <button onClick={() => del(h)} className="text-zinc-500 hover:text-red-400" data-testid="delete-webhook-btn"><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="mt-3"><div className="eyebrow mb-1">Secret de signature</div><div className="mono-box flex justify-between gap-2"><span data-testid="webhook-secret">{h.secret}</span><CopyButton value={h.secret} label="Secret copié" testId="copy-webhook-secret" /></div></div>
          </div>
        ))}
      </div>
      <div className="eyebrow mb-3">Journal des envois</div>
      {inbox.length > 0 && (
        <div className="panel overflow-x-auto mb-8" data-testid="webhook-inbox-table">
          <div className="p-3 text-xs text-zinc-400 border-b border-border">Événements <b>reçus</b> par le récepteur de test intégré (signature HMAC-SHA256 + horodatage vérifiés côté récepteur)</div>
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-zinc-500 border-b border-border"><tr><th className="p-3">Événement</th><th className="p-3">Commande</th><th className="p-3">Statut dans le payload</th><th className="p-3">Signature</th><th className="p-3">Reçu le</th></tr></thead>
            <tbody>{inbox.map((m) => (
              <tr key={m.id} className="border-b border-border/60" data-testid="webhook-inbox-row">
                <td className="p-3 font-mono text-xs">{m.event}</td>
                <td className="p-3 font-mono text-xs text-zinc-400">{m.payload?.data?.order?.id}</td>
                <td className="p-3"><StatusBadge status={m.payload?.data?.order?.status} /></td>
                <td className="p-3 text-xs" data-testid="inbox-signature-status">{m.signature_valid ? <span className="text-emerald-400">✓ valide</span> : <span className="text-red-400">✗ invalide</span>}</td>
                <td className="p-3 text-xs text-zinc-500">{fmtDate(m.received_at)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {logs.length === 0 ? <Empty text="Aucun envoi pour l'instant." /> : (
        <div className="panel overflow-x-auto" data-testid="webhook-logs-table">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-zinc-500 border-b border-border"><tr><th className="p-3">Événement</th><th className="p-3">Commande</th><th className="p-3">Statut</th><th className="p-3">HTTP</th><th className="p-3">Tentatives</th><th className="p-3">Date</th><th className="p-3"></th></tr></thead>
            <tbody>
              {logs.map((d) => (
                <tr key={d.id} className="border-b border-border/60" data-testid="webhook-log-row">
                  <td className="p-3 font-mono text-xs">{d.event}</td>
                  <td className="p-3 font-mono text-xs text-zinc-400">{d.order_id}</td>
                  <td className="p-3"><StatusBadge status={d.status} /></td>
                  <td className="p-3 text-xs">{d.last_status_code || <span className="text-red-400">{d.last_error ? d.last_error.slice(0, 40) : "—"}</span>}</td>
                  <td className="p-3 text-xs">{d.attempts}</td>
                  <td className="p-3 text-xs text-zinc-500">{fmtDate(d.created_at)}</td>
                  <td className="p-3 text-right"><button onClick={() => resend(d)} className="text-xs text-amber-400 hover:underline" data-testid="resend-webhook-btn">Renvoyer</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
